import React from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { motion } from 'framer-motion';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Send, 
  Calendar, 
  MessageSquare, 
  Car,
  CheckCircle2
} from 'lucide-react';
import { IMAGES } from '@/assets/images';
import { cn } from '@/lib/index';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';

const contactFormSchema = z.object({
  name: z.string().min(2, { message: 'Name muss mindestens 2 Zeichen lang sein.' }),
  email: z.string().email({ message: 'Ungültige E-Mail-Adresse.' }),
  phone: z.string().min(6, { message: 'Bitte geben Sie eine gültige Telefonnummer an.' }),
  vehicle: z.string().min(2, { message: 'Bitte geben Sie Ihr Fahrzeugmodell an.' }),
  message: z.string().min(10, { message: 'Die Nachricht sollte mindestens 10 Zeichen lang sein.' }),
});

type ContactFormValues = z.infer<typeof contactFormSchema>;

/**
 * Custom Zod resolver to fix module resolution error for @hook-form/resolvers/zod
 */
const zodResolver = (schema: z.ZodSchema) => async (values: any) => {
  const result = await schema.safeParseAsync(values);
  if (result.success) {
    return { values: result.data, errors: {} };
  }
  return {
    values: {},
    errors: result.error.issues.reduce((acc: any, issue) => {
      const path = issue.path.join('.');
      acc[path] = {
        message: issue.message,
        type: issue.code,
      };
      return acc;
    }, {}),
  };
};

const contactDetails = [
  {
    icon: Mail,
    title: 'E-Mail',
    value: 'Dellendoktor@tuta.com',
    description: 'Wir antworten innerhalb von 24h',
    href: 'mailto:Dellendoktor@tuta.com',
  },
  {
    icon: MapPin,
    title: 'Servicegebiet',
    value: 'Bayern und Baden-Württemberg',
    description: 'Mobiler Service vor Ort',
    href: '#',
  },
  {
    icon: Calendar,
    title: 'Termine',
    value: 'Nach Vereinbarung',
    description: 'Flexible Terminplanung',
    href: 'mailto:Dellendoktor@tuta.com',
  },
];

const businessHours = [
  { day: 'Montag - Freitag', hours: 'Nach Terminabsprache' },
  { day: 'Samstag', hours: 'Nach Terminabsprache' },
  { day: 'Sonntag', hours: 'Nach Terminabsprache' },
];

export default function Contact() {
  const { toast } = useToast();
  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      vehicle: '',
      message: '',
    },
  });

  function onSubmit(data: ContactFormValues) {
    console.log(data);
    toast({
      title: 'Anfrage gesendet!',
      description: 'Vielen Dank für Ihre Nachricht. Wir melden uns in Kürze bei Ihnen.',
    });
    form.reset();
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-foreground overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src={IMAGES.MECHANIC_WORK_1}
            alt="Werkstatt Background" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground to-transparent" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="text-primary font-mono text-sm tracking-widest uppercase mb-4 block">
              Kontakt aufnehmen
            </span>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Wir freuen uns auf Ihre <span className="text-primary">Anfrage</span>
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Haben Sie eine Delle oder einen Hagelschaden? Kontaktieren Sie Firat Karabacak für professionelle 
              Reparaturen in Bayern und Baden-Württemberg. Mobiler Service vor Ort nach Terminabsprache.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            
            {/* Contact Information */}
            <div className="lg:col-span-5 space-y-12">
              <div className="space-y-8">
                <h2 className="text-3xl font-bold">Kontaktinformationen</h2>
                <div className="grid gap-6">
                  {contactDetails.map((detail, idx) => (
                    <motion.a
                      key={detail.title}
                      href={detail.href}
                      target={detail.title === 'Standort' ? '_blank' : undefined}
                      rel={detail.title === 'Standort' ? 'noopener noreferrer' : undefined}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      className="flex items-start gap-4 p-4 rounded-xl border border-border bg-card hover:border-primary/50 transition-colors group"
                    >
                      <div className="p-3 rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                        <detail.icon size={24} />
                      </div>
                      <div>
                        <p className="font-mono text-xs text-muted-foreground uppercase">{detail.title}</p>
                        <p className="text-lg font-semibold">{detail.value}</p>
                        <p className="text-sm text-muted-foreground">{detail.description}</p>
                      </div>
                    </motion.a>
                  ))}
                </div>
              </div>

              <Card className="border-primary/20 bg-secondary/30 backdrop-blur">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3 mb-6">
                    <Clock className="text-primary" />
                    <h3 className="text-xl font-bold">Öffnungszeiten</h3>
                  </div>
                  <div className="space-y-4">
                    {businessHours.map((item) => (
                      <div key={item.day} className="flex justify-between items-center border-b border-border/50 pb-2">
                        <span className="text-muted-foreground">{item.day}</span>
                        <span className="font-mono font-medium">{item.hours}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-4">
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <CheckCircle2 className="text-primary" size={20} />
                  Warum wir?
                </h3>
                <ul className="grid gap-3 text-muted-foreground">
                  <li className="flex items-center gap-2">• Kostenvoranschlag per Foto (WhatsApp möglich)</li>
                  <li className="flex items-center gap-2">• Abrechnung direkt mit Ihrer Versicherung</li>
                  <li className="flex items-center gap-2">• Modernste PDR-Technik für perfekte Ergebnisse</li>
                  <li className="flex items-center gap-2">• Werterhalt Ihres Originallackes</li>
                </ul>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-7">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-card border border-border rounded-2xl p-8 shadow-xl shadow-primary/5"
              >
                <div className="mb-8">
                  <h2 className="text-3xl font-bold mb-2">Terminanfrage</h2>
                  <p className="text-muted-foreground">
                    Füllen Sie das Formular aus und wir melden uns schnellstmöglich für eine erste Einschätzung.
                  </p>
                </div>

                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Name</FormLabel>
                            <FormControl>
                              <Input placeholder="Ihr Name" {...field} className="bg-background" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>E-Mail</FormLabel>
                            <FormControl>
                              <Input placeholder="beispiel@mail.de" {...field} className="bg-background" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Telefonnummer</FormLabel>
                            <FormControl>
                              <div className="relative">
                                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                                <Input placeholder="+49 ..." {...field} className="bg-background pl-10" />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="vehicle"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Fahrzeugmodell</FormLabel>
                            <FormControl>
                              <div className="relative">
                                <Car className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                                <Input placeholder="z.B. VW Golf 8" {...field} className="bg-background pl-10" />
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="message"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Ihre Nachricht</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <MessageSquare className="absolute left-3 top-3 text-muted-foreground" size={18} />
                              <Textarea 
                                placeholder="Beschreiben Sie den Schaden kurz..." 
                                {...field} 
                                className="min-h-[150px] bg-background pl-10 pt-2"
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button type="submit" className="w-full py-6 text-lg group">
                      Anfrage absenden
                      <Send className="ml-2 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" size={18} />
                    </Button>

                    <p className="text-center text-xs text-muted-foreground">
                      Mit dem Absenden erklären Sie sich mit unserer Datenschutzerklärung einverstanden.
                    </p>
                  </form>
                </Form>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Area Map */}
      <section className="h-[400px] w-full bg-muted relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
          <div className="text-center space-y-6">
            <div className="p-6 bg-primary rounded-full text-white mx-auto w-fit">
              <MapPin size={48} />
            </div>
            <div className="space-y-2">
              <h3 className="text-2xl font-bold">Mobiler Service</h3>
              <p className="text-lg text-muted-foreground">Bayern und Baden-Württemberg</p>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                Wir kommen zu Ihnen! Professionelle Dellenreparatur direkt vor Ort nach Terminabsprache.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Bar */}
      <section className="py-12 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="flex items-center gap-6">
            <div className="hidden sm:block p-4 bg-white/10 rounded-full">
              <Calendar size={32} />
            </div>
            <div>
              <h3 className="text-2xl font-bold">Hagelschaden-Saison 2026?</h3>
              <p className="text-primary-foreground/80">Wir bieten Vor-Ort-Besichtigungen für Versicherungen an.</p>
            </div>
          </div>
          <Button variant="outline" className="bg-transparent border-white text-white hover:bg-white hover:text-primary min-w-[200px] h-14">
            Jetzt Beraten Lassen
          </Button>
        </div>
      </section>
    </div>
  );
}